import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Route as TenantLayoutRoute } from "./_layout";
import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Modal } from "@/components/application/modals/modal-controlled";
import { useToast } from "@/components/application/notifications/toast-provider";
import { ApiError } from "../../lib/apiClient";
import {
  INVITABLE_ROLES,
  INVITABLE_ROLE_LABELS,
  USER_INVITATION_STATUS_LABELS,
  type UserInvitationStatus,
} from "../../schemas/userInvite";
import {
  useCreateTenantInvitation,
  useRevokeTenantInvitation,
  useTenantInvitations,
  useTenantUsers,
  type TenantInvitation,
  type TenantUser,
} from "../../hooks/useTenantUsers";

/** Uživatelé střediska (tenantAdmin) — seznam + pozvánky do vlastního tenanta. */

function TenantUsersPage() {
  const users = useTenantUsers();
  const invites = useTenantInvitations();
  const revoke = useRevokeTenantInvitation();
  const toast = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);

  async function onRevoke(row: TenantInvitation) {
    try {
      await revoke.mutateAsync(row.id);
      toast.push({ variant: "success", message: `Pozvánka pro ${row.email} zrušena.` });
    } catch (e) {
      toast.push({ variant: "error", message: e instanceof Error ? e.message : "Zrušení selhalo." });
    }
  }

  const userColumns: Column<TenantUser>[] = [
    { key: "name", header: "Jméno", cell: (r) => <strong className="text-primary">{r.display_name}</strong> },
    { key: "email", header: "E-mail", cell: (r) => r.email },
    { key: "role", header: "Role", cell: (r) => <Badge color="blue">{INVITABLE_ROLE_LABELS[r.role as keyof typeof INVITABLE_ROLE_LABELS] ?? r.role}</Badge> },
    {
      key: "status",
      header: "Stav",
      cell: (r) =>
        r.deactivated_at ? <Badge color="warning">Deaktivován</Badge> : <BadgeWithDot color="success">Aktivní</BadgeWithDot>,
    },
  ];

  const inviteColumns: Column<TenantInvitation>[] = [
    {
      key: "name",
      header: "Jméno",
      cell: (r) => (
        <div className="flex flex-col gap-0.5">
          <strong className="text-primary">{r.first_name} {r.last_name}</strong>
          <span className="text-xs text-tertiary">{r.email}</span>
        </div>
      ),
    },
    { key: "role", header: "Role", cell: (r) => <Badge color="blue">{INVITABLE_ROLE_LABELS[r.role as keyof typeof INVITABLE_ROLE_LABELS] ?? r.role}</Badge> },
    {
      key: "status",
      header: "Stav",
      cell: (r) => <Badge color={r.status === "pending" ? "warning" : "gray"}>{USER_INVITATION_STATUS_LABELS[r.status as UserInvitationStatus] ?? r.status}</Badge>,
    },
    {
      key: "actions",
      header: "Akce",
      cell: (r) =>
        r.status === "pending" ? (
          <Button size="sm" color="secondary" onPress={() => void onRevoke(r)}>Zrušit</Button>
        ) : (
          <span className="text-quaternary">—</span>
        ),
    },
  ];

  return (
    <section aria-labelledby="tenant-users-title" className="max-w-7xl mx-auto space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 id="tenant-users-title" className="text-2xl font-bold text-primary m-0">Uživatelé</h2>
          <p className="text-base text-tertiary m-0">Uživatelé střediska a pozvánky do vašeho tenanta.</p>
        </div>
        <Button color="primary" onPress={() => setInviteOpen(true)} data-testid="tenant-invite-open">
          Pozvat uživatele
        </Button>
      </header>

      <Card>
        <CardHeader><h3 className="text-lg font-semibold text-primary m-0">Uživatelé tenanta</h3></CardHeader>
        <CardBody>
          {users.isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : users.error ? (
            <InlineAlert variant="error">{users.error instanceof Error ? users.error.message : "Načtení selhalo."}</InlineAlert>
          ) : (
            <DataTable caption="Uživatelé tenanta" columns={userColumns} rows={users.data?.users ?? []} rowKey={(r) => r.user_id} emptyMessage="Žádní uživatelé." />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader><h3 className="text-lg font-semibold text-primary m-0">Pozvánky</h3></CardHeader>
        <CardBody>
          {invites.isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : (
            <DataTable caption="Pozvánky tenanta" columns={inviteColumns} rows={invites.data?.rows ?? []} rowKey={(r) => r.id} emptyMessage="Žádné pozvánky." />
          )}
        </CardBody>
      </Card>

      {inviteOpen && <InviteModal onClose={() => setInviteOpen(false)} />}
    </section>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("endUser");
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateTenantInvitation();
  const toast = useToast();

  async function submit() {
    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setFormError("Jméno a příjmení musí mít alespoň 2 znaky.");
      return;
    }
    if (!email.includes("@")) {
      setFormError("Neplatný e-mail.");
      return;
    }
    setFormError(null);
    try {
      await create.mutateAsync({ first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim(), role });
      toast.push({ variant: "success", message: `Pozvánka odeslána na ${email.trim()}.` });
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.errorCode === "USER_INVITATION_DUPLICATE_PENDING") {
        setFormError("Pro tento e-mail už existuje aktivní pozvánka.");
      } else if (e instanceof ApiError && e.errorCode === "USER_INVITATION_USER_EXISTS") {
        setFormError("Uživatel s tímto e-mailem už existuje.");
      } else {
        setFormError(e instanceof Error ? e.message : "Odeslání selhalo.");
      }
    }
  }

  const submitting = create.isPending;

  return (
    <Modal
      open
      title="Pozvat uživatele"
      onClose={submitting ? () => {} : onClose}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={submitting}>Zrušit</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={submitting} isLoading={submitting} data-testid="tenant-invite-submit">
            Odeslat pozvánku
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError && <InlineAlert variant="error">{formError}</InlineAlert>}
        <Input label="Jméno" isRequired value={firstName} onChange={setFirstName} isDisabled={submitting} />
        <Input label="Příjmení" isRequired value={lastName} onChange={setLastName} isDisabled={submitting} />
        <Input label="E-mail" isRequired type="email" value={email} onChange={setEmail} isDisabled={submitting} />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="tenant-invite-role" className="text-sm font-medium text-secondary">
            Role <span className="text-brand-tertiary">*</span>
          </label>
          <select
            id="tenant-invite-role"
            className="block w-full min-h-touch rounded-lg bg-primary px-3 py-2 text-md text-primary ring-1 ring-inset ring-primary outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-50"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={submitting}
            data-testid="tenant-invite-role-select"
          >
            {INVITABLE_ROLES.map((r) => (
              <option key={r} value={r}>{INVITABLE_ROLE_LABELS[r]}</option>
            ))}
          </select>
        </div>
      </div>
    </Modal>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/users",
  component: TenantUsersPage,
});
